<template>
	<div
		class="genogram-legend-symbol-icon"
		:class="wrapperClassComputed"
		:style="wrapperStyleComputed"
	>
		<div v-if="sexualCommunicationLabelComputed" class="genogram-legend-symbol-icon__badge">
			{{ sexualCommunicationLabelComputed }}
		</div>
		<svg v-if="isPrimaryPerson" class="genogram-legend-symbol-icon__frame" viewBox="0 0 100 100" aria-hidden="true">
			<circle
				cx="50"
				cy="50"
				:r="circleFrameRadiusComputed"
				fill="none"
				:stroke="frameColorComputed"
				:stroke-width="frameStrokeWidthComputed"
			/>
		</svg>
		<div class="genogram-legend-symbol-icon__image-wrap">
			<img
				v-if="shouldShowBaseSymbolComputed"
				:src="src"
				:alt="alt"
				class="genogram-legend-symbol-icon__image"
			>
			<svg v-if="isPassedAway" class="genogram-legend-symbol-icon__overlay" viewBox="0 0 100 100" aria-hidden="true">
				<path
					d="M18 18 L82 82 M18 82 L82 18"
					fill="none"
					stroke="rgba(20, 20, 20, 0.9)"
					stroke-width="12"
					stroke-linecap="round"
				/>
			</svg>
		</div>
	</div>
</template>

<script>
import {shapeFromType} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/defaults/node-types";
import {
	PRIMARY_PERSON_LEGEND_HIGHLIGHT_COLOR,
	PRIMARY_PERSON_LEGEND_HIGHLIGHT_LINE_WIDTH,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramPrimaryPersonHelper";
import {getSexualCommunicationLabel} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramSexualCommunicationHelper";

const PRIMARY_PERSON_LEGEND_DEFAULT_INSET_PERCENT = 18
const PRIMARY_PERSON_LEGEND_ANGULAR_INSET_PERCENT = 22
const PRIMARY_PERSON_LEGEND_FRAME_SAFETY_INSET = 2

export default {
	name: "GenogramLegendSymbolIcon",

	props: {
		src: {type: String, default: ""},
		alt: {type: String, default: ""},
		type: {type: String, default: ""},
		size: {type: Number, default: 28},
		isPrimaryPerson: Boolean,
		isPassedAway: Boolean,
		sexualCommunication: {type: String, default: ""},
	},

	computed: {
		shapeComputed() {
			let shape = shapeFromType(this.type)
			if(shape === "ellipse" || shape === "diamond" || shape === "triangle"){
				return shape
			}
			return "rectangle"
		},
		wrapperClassComputed() {
			return {
				[`genogram-legend-symbol-icon--${this.shapeComputed}`]: true,
				"genogram-legend-symbol-icon--primary": this.isPrimaryPerson,
				"genogram-legend-symbol-icon--badge-only": !this.src,
			}
		},
		wrapperStyleComputed() {
			return {
				"--legend-symbol-size": `${this.size}px`,
				"--legend-primary-person-image-inset": `${this.primaryPersonInsetPercentComputed}%`,
				"--legend-badge-font-size": `${Math.max(8, Math.round(this.size * 0.42))}px`,
			}
		},
		sexualCommunicationLabelComputed() {
			return getSexualCommunicationLabel(this.sexualCommunication)
		},
		primaryPersonInsetPercentComputed() {
			if(this.shapeComputed === "ellipse"){
				return PRIMARY_PERSON_LEGEND_DEFAULT_INSET_PERCENT
			}
			return PRIMARY_PERSON_LEGEND_ANGULAR_INSET_PERCENT
		},
		shouldShowBaseSymbolComputed() {
			if(!this.src){
				return false
			}
			if(!this.isPassedAway){
				return true
			}
			return this.shapeComputed === "triangle"
		},
		frameStrokeWidthComputed() {
			return PRIMARY_PERSON_LEGEND_HIGHLIGHT_LINE_WIDTH
		},
		circleFrameRadiusComputed() {
			return 50 - this.frameStrokeWidthComputed / 2 - PRIMARY_PERSON_LEGEND_FRAME_SAFETY_INSET
		},
		frameColorComputed() {
			return PRIMARY_PERSON_LEGEND_HIGHLIGHT_COLOR
		},
	},
}
</script>

<style lang="scss">
.genogram-legend-symbol-icon{
	position: relative;
	flex: 0 0 auto;
	width: var(--legend-symbol-size);
	height: var(--legend-symbol-size);
}

.genogram-legend-symbol-icon__badge{
	position: absolute;
	left: 0;
	top: 0;
	transform: translate(-28%, -72%);
	z-index: 3;
	padding: 1px 4px;
	border-radius: 3px;
	background: rgba(255, 255, 255, 0.88);
	color: #111827;
	font-size: var(--legend-badge-font-size);
	line-height: 1.15;
	white-space: nowrap;
	pointer-events: none;
}

.genogram-legend-symbol-icon--badge-only .genogram-legend-symbol-icon__badge{
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
}

.genogram-legend-symbol-icon__frame,
.genogram-legend-symbol-icon__image-wrap{
	position: absolute;
	inset: 0;
}

.genogram-legend-symbol-icon__image-wrap{
	display: flex;
	align-items: center;
	justify-content: center;
}

.genogram-legend-symbol-icon--primary .genogram-legend-symbol-icon__image-wrap{
	inset: var(--legend-primary-person-image-inset);
}

.genogram-legend-symbol-icon__image{
	display: block;
	width: 100%;
	height: 100%;
	object-fit: contain;
	position: relative;
	z-index: 1;
}

.genogram-legend-symbol-icon__frame{
	pointer-events: none;
}

.genogram-legend-symbol-icon__overlay{
	position: absolute;
	inset: 0;
	pointer-events: none;
	z-index: 2;
}
</style>
